/**
 * settingsGroups.js — Iter 191t · Settings Hub Overhaul (Msg 598)
 *
 * Single source of truth for the Squarespace-style Settings left-rail.
 * Each item has: { id, label, to, icon, status?, badge? }
 *
 * `status` values:
 *   • 'ready' — existing sub-page renders in the right pane
 *   • 'stub'  — page exists but is a placeholder (Wave 2 build)
 *   • 'soon'  — greyed / not clickable (backlog)
 *
 * Route paths are all rooted at /website-builder/settings/*
 */
import {
  Settings, Globe, Languages, Cookie, Image as ImageIcon,
  FileSearch, Radio, Layers, Mail, ShoppingBag, CreditCard, Truck,
  Palette, TrendingUp, LineChart, Plug, Users, KeyRound, Wallet,
  Receipt, Heart, Bell, ScrollText, Download, Rss, MapPin,
  Target, ShieldCheck, Lock,
} from 'lucide-react';

const R = (path) => `/website-builder/settings/${path}`;

export const SETTINGS_GROUPS = [
  {
    id: 'website',
    label: 'Website',
    items: [
      { id: 'general',        label: 'General',            to: R('general'),         icon: Settings,   status: 'ready' },
      { id: 'availability',   label: 'Site Availability',  to: R('site-availability'), icon: KeyRound, status: 'ready' },
      { id: 'languages',      label: 'Site Languages',     to: R('languages'),       icon: Languages,  status: 'ready' },
      { id: 'cookies',        label: 'Cookies & Visitor Data', to: R('cookies'),     icon: Cookie,     status: 'ready' },
      { id: 'favicon',        label: 'Logo & Favicon',     to: R('favicon'),         icon: ImageIcon,  status: 'ready' },
      { id: 'seo',            label: 'SEO',                to: R('seo'),             icon: FileSearch, status: 'ready' },
      { id: 'blog',           label: 'Blog Preferences',   to: R('blog'),            icon: Rss,        status: 'ready' },
    ],
  },
  {
    id: 'domains-email',
    label: 'Domains & Email',
    items: [
      { id: 'domains',        label: 'Custom Domains',    to: R('domains'),        icon: Globe,      status: 'ready' },
      { id: 'workspace',      label: 'Google Workspace',  to: R('workspace'),      icon: Mail,       status: 'ready' },
      { id: 'branded-emails', label: 'Branded Emails',    to: R('branded-emails'), icon: Mail,       status: 'ready' },
    ],
  },
  {
    id: 'selling',
    label: 'Selling',
    items: [
      { id: 'payments',      label: 'Payments',                  to: R('payments'),          icon: Wallet,      status: 'ready' },
      { id: 'shipping',      label: 'Shipping & Delivery',       to: R('shipping'),          icon: Truck,       status: 'ready' },
      { id: 'location',      label: 'Location & Hours',          to: R('location'),          icon: MapPin,      status: 'ready' },
      { id: 'cart-recovery', label: 'Cart Recovery',              to: R('cart-recovery'),     icon: ShoppingBag, status: 'ready' },
      { id: 'checkout',      label: 'Checkout',                   to: R('checkout'),          icon: CreditCard,  status: 'ready' },
      { id: 'reviews',       label: 'Product Reviews',            to: R('reviews'),           icon: TrendingUp,  status: 'ready' },
      { id: 'taxes',         label: 'Taxes & Duties',              to: R('taxes'),             icon: Receipt,     status: 'ready' },
      { id: 'donations',     label: 'Donations',                    to: R('donations'),         icon: Heart,       status: 'ready' },
      { id: 'customer-accounts', label: 'Customer Accounts',         to: R('customer-accounts'), icon: Users,       status: 'ready' },
    ],
  },
  {
    id: 'notifications',
    label: 'Customer Notifications',
    items: [
      { id: 'notifications', label: 'Email Templates',      to: R('notifications'),      icon: Bell,       status: 'ready' },
      { id: 'policies',      label: 'Policies',             to: R('policies'),           icon: ScrollText, status: 'ready' },
    ],
  },
  {
    id: 'brand-marketing',
    label: 'Brand & Marketing',
    items: [
      { id: 'pixels',      label: 'Pixels & Tags',       to: R('pixels'),       icon: Target,     status: 'ready' },
    ],
  },
  {
    id: 'integrations',
    label: 'Third-Party Tools',
    items: [
      { id: 'integrations', label: 'Integrations',       to: R('integrations'),  icon: Plug,       status: 'ready' },
    ],
  },
  {
    id: 'permissions',
    label: 'Permissions & Ownership',
    items: [
      { id: 'team',              label: 'Team & Contributors', to: R('team'),             icon: Users,      status: 'ready' },
      { id: 'privacy',           label: 'Privacy & Data',      to: R('privacy'),          icon: Lock,       status: 'ready' },
      { id: 'security',          label: 'Data & Security',     to: R('security'),         icon: ShieldCheck, status: 'ready' },
      { id: 'account-controls',  label: 'Account Controls',    to: R('account-controls'), icon: Download,    status: 'ready' },
    ],
  },
  {
    id: 'billing',
    label: 'Billing',
    items: [
      { id: 'billing', label: 'Plan & Billing',    to: R('billing'), icon: CreditCard, status: 'ready' },
    ],
  },
];

// Flat list for quick lookups (id → item)
export const SETTINGS_ITEMS_BY_ID = SETTINGS_GROUPS.reduce((acc, g) => {
  for (const item of g.items) acc[item.id] = { ...item, _groupId: g.id, _groupLabel: g.label };
  return acc;
}, {});
